import React, { useState, useRef, useEffect } from 'react';
import { videoRecorderService } from '../../services/videoRecorderService';
import { VideoRecording } from '../../types/videoRecorder';
import { useAuth } from '../../contexts/AuthContext';

interface VideoPlayerProps {
  video: VideoRecording;
  autoPlay?: boolean;
  controls?: boolean;
  onEnd?: () => void;
}

const VideoPlayerComponent: React.FC<VideoPlayerProps> = ({
  video,
  autoPlay = false,
  controls = true,
  onEnd
}) => {
  const { user } = useAuth();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [subtitleUrl, setSubtitleUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  
  useEffect(() => {
    const getVideoUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get a signed URL for the video
        const url = await videoRecorderService.getVideoSignedUrl(video.storage_path);
        
        if (!url) {
          throw new Error('Failed to get video URL');
        }
        
        setVideoUrl(url);

        // Load subtitle signed URL if available
        if (video.subtitle_path) {
          try {
            const sUrl = await videoRecorderService.getSubtitleSignedUrl(video.subtitle_path);
            setSubtitleUrl(sUrl);
          } catch (e) {
            console.warn('Subtitle load failed:', e);
            setSubtitleUrl(null);
          }
        } else {
          setSubtitleUrl(null);
        }
      } catch (err) {
        console.error('Error loading video:', err);
        setError('Failed to load video');
      } finally {
        setIsLoading(false);
      }
    };
    
    getVideoUrl();
    
    // Clean up URL object on unmount
    return () => {
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [video]);
  
  const handleVideoEnd = () => {
    if (onEnd) {
      onEnd();
    }
  };
  
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  if (isLoading) {
    return (
      <div className="relative bg-black aspect-video rounded-lg flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }
  
  if (error || !videoUrl) {
    return (
      <div className="relative bg-gray-900 aspect-video rounded-lg flex justify-center items-center">
        <div className="text-white text-center p-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>{error || 'Video not available'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative bg-black rounded-lg overflow-hidden">
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full aspect-video"
        autoPlay={autoPlay}
        controls={controls}
        onEnded={handleVideoEnd}
        playsInline
      >
        {subtitleUrl && (
          // Track must be a child of video for captions to work
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <track
            kind="subtitles"
            srcLang="en"
            label="English"
            src={subtitleUrl}
            default
          />
        )}
      </video>
      
      {video.duration_seconds && !controls && (
        <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
          {formatTime(video.duration_seconds)}
        </div>
      )}
    </div>
  );
};

export default VideoPlayerComponent;
